import axios from 'axios';
import {
  addToQueue,
  saveAuthToken,
  requestBackgroundSync,
  cacheData,
  getCachedData,
} from '../utils/offlineDB';

const getBaseUrl = () => {
  // Priority order: VITE env, then CRA env, then manual origin check
  if (process.env.VITE_API_URL) return process.env.VITE_API_URL;
  if (process.env.REACT_APP_API_BASE_URL) return process.env.REACT_APP_API_BASE_URL;
  
  if (window.location.origin.includes('localhost')) {
    return 'http://localhost:5000';
  }
  
  // If user is on the custom .in domain, use the custom .in backend
  if (window.location.origin.includes('naturextraktefertilisers.in')) {
    return 'https://backend.naturextraktefertilisers.in';
  }
  
  // Default fallback (e.g. Vercel frontend connects to Render backend)
  return 'https://obfs.onrender.com';
};

const API = axios.create({
  baseURL: getBaseUrl(),
});

// ─── Request Interceptor ───────────────────────────────────────────
// Attach auth token AND persist it to IndexedDB for Service Worker access
API.interceptors.request.use((req) => {
  if (localStorage.getItem('userInfo')) {
    const token = JSON.parse(localStorage.getItem('userInfo')).token;
    req.headers.Authorization = `Bearer ${token}`;

    // Persist token so the Service Worker can read it from IndexedDB
    saveAuthToken(token).catch(() => {});
  }
  return req;
});

// ─── Helpers ───────────────────────────────────────────────────────

/** Map a request URL to an offline cache store name, or null if not cacheable */
const getCacheStore = (url) => {
  if (!url) return null;
  if (url.includes('/fields'))            return 'fields';
  if (url.includes('/crops'))             return 'crops';
  if (url.includes('/recommendations'))   return 'recommendations';
  // Extended offline caching — store in 'fields' as generic cache
  if (url.includes('/soil-reports'))       return 'fields';
  if (url.includes('/irrigation'))         return 'fields';
  if (url.includes('/fertilizer'))         return 'fields';
  if (url.includes('/field-issues'))       return 'fields';
  if (url.includes('/users/profile'))      return 'fields';
  return null;
};

const isGetRequest   = (config) => (config.method || 'get').toLowerCase() === 'get';
const isMutation     = (config) => ['post', 'put', 'patch', 'delete'].includes((config.method || '').toLowerCase());
const NETWORK_ERROR_CODES = ['ERR_NETWORK', 'ERR_INTERNET_DISCONNECTED', 'ERR_FAILED', 'ECONNABORTED'];
const isNetworkError = (error) => {
  if (error.response) return false; // Got a server response — not a network error
  if (!navigator.onLine) return true;
  if (NETWORK_ERROR_CODES.includes(error.code)) return true;
  if (error.message?.includes('Network Error')) return true;
  return false;
};

// ─── Response Interceptor ──────────────────────────────────────────
API.interceptors.response.use(
  // ── SUCCESS: cache GET responses for offline use ──
  async (response) => {
    const config = response.config;

    if (isGetRequest(config)) {
      const storeName = getCacheStore(config.url);
      if (storeName) {
        const cacheKey = config.url; // full relative URL incl. query params
        try {
          await cacheData(storeName, cacheKey, response.data);
        } catch (e) {
          // caching failure is non-critical
        }
      }
    }

    return response;
  },

  // ── ERROR: handle offline / network errors ──
  async (error) => {
    const config = error.config;

    if (isNetworkError(error) && config) {
      // ── GET — serve stale data from IndexedDB ──
      if (isGetRequest(config)) {
        const storeName = getCacheStore(config.url);
        if (storeName) {
          try {
            const cached = await getCachedData(storeName, config.url);
            if (cached !== null) {
              return {
                data: cached,
                status: 200,
                statusText: 'OK (offline cache)',
                headers: {},
                config,
                _fromOfflineCache: true,
              };
            }
          } catch (e) {
            // cache read failed, continue to reject
          }
        }
        // No cached data — tag error so components can show offline-friendly UI
        const offlineError = new Error('You are offline. This data will load when you reconnect.');
        offlineError._isOffline = true;
        offlineError.config = config;
        return Promise.reject(offlineError);
      }

      // ── Mutation — queue for later sync ──
      if (isMutation(config)) {
        // SENIOR DEV FIX: Skip queuing for non-critical analytical tracking
        if (config.url?.includes('/view') || config.url?.includes('/analytics')) {
          return Promise.reject(error);
        }

        // CRITICAL FIX: Skip queuing for interactive auth and security requests.
        // Users must be online to login, register, or manage passwords.
        const isAuthRequest = config.url?.includes('/users/login') || 
                            config.url?.includes('/users/register') || 
                            config.url?.includes('/users/change-password') ||
                            config.url?.includes('/password-reset');
        
        if (isAuthRequest) {
          const authError = new Error('Authentication requires an active internet connection. Please connect and try again.');
          authError._isAuthError = true;
          return Promise.reject(authError);
        }

        try {
          const payload = config.data;
          const fullUrl = `${config.baseURL || getBaseUrl()}${config.url}`;
          const clientId = await addToQueue(fullUrl, config.method, payload);

          // Ask the Service Worker to sync when connectivity returns
          requestBackgroundSync();

          return {
            data: { _offlineQueued: true, clientId },
            status: 202,
            statusText: 'Accepted (queued offline)',
            headers: {},
            config,
            _offlineQueued: true,
          };
        } catch (queueError) {
          console.error('[Axios] Failed to queue offline mutation:', queueError);
          return Promise.reject(error);
        }
      }
    }

    return Promise.reject(error);
  }
);

export default API;
